// 2026-07-23 极简 composer: MailEditor — 纯受控编辑器。
//
// 设计:
//   - 不持有任何 React state(除了 Cc 显隐这种纯 UI 折叠状态)
//   - 接 value + onChange,Editor 显示什么完全由 caller 决定
//   - 不调 API
//   - 不做 autosave / dirty / save status
//   - 不知道自己是「新建邮件」、「编辑 Draft」、还是「Reply」
//
// 单一数据源原则:
//   caller 持 useDraftForm 提供的 { draft, setField }
//   MailEditor 只用 draft + setField 渲染,setField 由 caller 包装(字段名映射 to → to_addresses 等)
//
// UI 子组件全部沿用既有 MailRecipientsFields / RichTextEditorCore /
// MailAttachmentsChips / MailComposerToolbar / SaveStatusBadge。
// 本文件只做编排,不做新 UI。

import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { MailRecipientsFields } from "./MailRecipientsFields";
import { MailAttachmentsChips } from "./MailAttachmentsChips";
import { MailComposerToolbar } from "./MailComposerToolbar";
import { RichTextEditorCore } from "./RichTextEditorCore";
import { plainTextToHtml } from "./plainTextHtml";
import type { MailComposerFields, SaveStatus } from "./mailComposerTypes";
import type { MailAttachmentDTO } from "../../../api/mail-v2";

export interface MailEditorProps {
  // ---- 受控字段(由 caller 的 useDraftForm 提供)----
  values: MailComposerFields;
  onChange: <K extends keyof MailComposerFields>(key: K, value: MailComposerFields[K]) => void;

  // ---- 模式标签(只影响 data-mode + SaveStatusBadge 文案;不影响行为)----
  mode: "new" | "edit-existing" | "reply";

  // ---- Save 状态(由 caller 派生,MailEditor 只展示)----
  saveStatus: SaveStatus;
  lastSavedAt?: string | null;

  // ---- 发送 / 删除回调(由 caller 注入真实实现)----
  sending: boolean;
  canSend: boolean;
  onSend: () => void;
  onDeleteDraft?: () => void;
  hasDraft: boolean;
  deleting?: boolean;

  // ---- 附件(由 caller 持有 useAttachments,把 items + handlers 传进来)----
  attachments?: MailAttachmentDTO[];
  uploadingCount?: number;
  onAttachFiles?: (files: FileList | null) => void;
  onRemoveAttachment?: (attachmentId: string) => void;
  /** 2026-07-24: AI 生成按钮 click handler,透传到 MailComposerToolbar。 */
  onAiClick?: () => void;

  // ---- 编辑器 key(由 caller 给的 sessionKey 派生;让 RichTextEditorCore 在切换 draft 时 remount)----
  editorKey: string;

  // ---- 测试用 ----
  "data-testid"?: string;
}

/**
 * MailEditor — 纯受控编辑器。
 *
 * 不持有任何业务 state;全部状态来自 props。
 * 不调 API;所有副作用通过 onSend / onDeleteDraft / onAttachFiles 回调给 caller。
 *
 * Cc 显隐是本组件唯一的本地 state(纯 UI 折叠,不进入 lastSavedDirty 判定)。
 */
export function MailEditor({
  values,
  onChange,
  mode,
  saveStatus,
  sending,
  canSend,
  onSend,
  // onDeleteDraft / hasDraft / deleting / lastSavedAt 不再被 toolbar 消费(2026-07-24
  // 简化 footer),但 MailEditorProps 保留这些 字段,MailV2PageComposer 仍可传。
  attachments,
  uploadingCount,
  onAttachFiles,
  onRemoveAttachment,
  onAiClick,
  editorKey,
  "data-testid": dataTestId,
}: MailEditorProps) {
  // ---- 唯一本地 UI state:Cc 显隐(纯折叠,不参与 dirty)----
  const [showCc, setShowCc] = useState<boolean>(!!values.cc);
  useEffect(() => {
    if (values.cc.trim()) {
      setShowCc(true);
    }
  }, [values.cc]);

  // ---- 附件 input ref ----
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);

  // ---- editor 初值:优先 bodyHtml,fallback bodyText → plainTextToHtml ----
  const editorInitialHtml = values.bodyHtml
    ? values.bodyHtml
    : values.bodyText
      ? plainTextToHtml(values.bodyText)
      : "";

  const handleEditorChange = (next: { html: string; text: string }) => {
    const hasRichFormatting = next.html !== next.text;
    onChange("bodyHtml", hasRichFormatting ? next.html : "");
    onChange("bodyText", next.text);
  };

  const handleAttachClick = () => {
    if (!attachmentInputRef.current) return;
    attachmentInputRef.current.value = "";
    attachmentInputRef.current.click();
  };

  const handleAttachFilesChange = (e: ChangeEvent<HTMLInputElement>) => {
    onAttachFiles?.(e.target.files);
  };

  return (
    <div
      className="mv2-compose-root"
      data-mode={mode}
      data-testid={dataTestId ?? "mv2-compose-root"}
    >
      <MailRecipientsFields
        values={values}
        onChange={onChange}
        disabled={sending}
        showCc={showCc}
        onToggleCc={setShowCc}
      />

      <RichTextEditorCore
        key={editorKey}
        value={editorInitialHtml}
        onChange={handleEditorChange}
        placeholder="本文を入力"
        disabled={sending}
        // 2026-07-25: 用 "100%" 让编辑区在 flex 列里填满剩余空间,
        // 而不是固定 240px。Compose dialog 加宽(尤其打开 AI drawer)时,
        // 编辑区自动延伸到底。
        minHeight="100%"
        data-testid="mv2-compose-body"
      />

      {attachments && (
        <MailAttachmentsChips
          items={attachments}
          onRemove={onRemoveAttachment}
          disabled={sending}
        />
      )}

      {saveStatus === "error" && (
        <div className="mv2-compose-error" role="alert" data-testid="mv2-compose-save-error">
          保存に失敗しました
        </div>
      )}

      <MailComposerToolbar
        sending={sending}
        canSend={canSend}
        onSend={onSend}
        onAttachClick={onAttachFiles ? handleAttachClick : undefined}
        uploadingCount={uploadingCount}
        onAiClick={onAiClick}
        attachmentInput={
          onAttachFiles && (
            <input
              ref={attachmentInputRef as React.RefObject<HTMLInputElement>}
              type="file"
              multiple
              style={{ display: "none" }}
              aria-hidden
              onChange={handleAttachFilesChange}
            />
          )
        }
      />
    </div>
  );
}
